import { defineComponent, onMounted, ref, computed, watch, nextTick, } from 'vue';
import Terminal from './Terminal.vue';
import Arrow from '../icons/Arrow.vue';
import Indicator from '../icons/Indicator.vue';
import Bin from '../icons/Bin.vue';
import { mouseDownHandler } from '../core/events';
import { terminalStore, MAIN_TERMINAL } from '../core/stores';
export default defineComponent({
    components: {
        Arrow,
        Indicator,
        Bin,
        Terminal,
    },
    setup() {
        const resizer = ref(null);
        const terminalWrapper = ref(null);
        const activeTerminal = ref(undefined);
        const isTerminalPanelOpened = ref(false);
        const terminalPanelHeight = ref(0);
        const arrowHovered = ref(false);
        const pipelineSpanClasses = (terminal) => ({
            active: isTerminalPanelOpened.value && terminal === activeTerminal.value,
        });
        const terminalWrapperStyles = computed(() => {
            const minTerminalPanelHeight = 395;
            if (isTerminalPanelOpened.value) {
                return {
                    height: `${Math.max(terminalPanelHeight.value, minTerminalPanelHeight)}px`,
                };
            }
            return {
                height: 'unset',
            };
        });
        const resizerStyles = computed(() => ({
            'pointer-events': (isTerminalPanelOpened.value ? 'all' : 'none'),
        }));
        onMounted(() => {
            const setTerminalHeight = (height) => {
                terminalPanelHeight.value = height;
            };
            resizer.value.addEventListener('mousedown', mouseDownHandler(setTerminalHeight));
        });
        const displayedTerminals = ref([]);
        // Computing tuples of names and lengths of terminals
        const terminalNamesLengths = computed(() => {
            const terminalLogs = Object.values(terminalStore.logs);
            const lengths = terminalLogs.map((log) => log.length);
            const names = Object.keys(terminalStore.logs);
            const tuples = [];
            for (let i = 0; i < names.length; i += 1) {
                tuples.push({
                    name: names[i],
                    length: lengths[i],
                });
            }
            return tuples;
        });
        watch(terminalNamesLengths, (newVal, oldVal) => {
            const oldNames = oldVal === null || oldVal === void 0 ? void 0 : oldVal.map((tuple) => tuple.name);
            newVal.forEach((tuple) => {
                if (!(oldNames === null || oldNames === void 0 ? void 0 : oldNames.includes(tuple.name))) {
                    // Adding new terminals to the displayed ones
                    displayedTerminals.value.push({
                        name: tuple.name,
                        hasNewMessage: tuple.length !== 0,
                    });
                }
                else {
                    // Checking whether length changed in an existing terminal
                    const oldTuple = oldVal.find((t) => t.name === tuple.name);
                    if (oldTuple.length !== tuple.length && tuple.name !== activeTerminal.value) {
                        const terminal = displayedTerminals.value.find((t) => t.name === tuple.name);
                        terminal.hasNewMessage = true;
                    }
                }
            });
        }, {
            immediate: true,
        });
        const terminalIndicatorColor = (hasNewMessage) => {
            if (hasNewMessage)
                return 'green';
            return 'gray';
        };
        const setReadMessages = (terminalName) => {
            displayedTerminals.value.forEach((terminal) => {
                // eslint-disable-next-line no-param-reassign
                if (terminal.name === terminalName)
                    terminal.hasNewMessage = false;
            });
        };
        const toggleTerminalPanel = (terminal) => {
            var _a, _b;
            if (terminal === undefined && !isTerminalPanelOpened.value) {
                activeTerminal.value = MAIN_TERMINAL;
                isTerminalPanelOpened.value = true;
                setReadMessages(MAIN_TERMINAL);
            }
            else if (terminal === activeTerminal.value || terminal === undefined) {
                activeTerminal.value = undefined;
                isTerminalPanelOpened.value = false;
            }
            else {
                // Keep the current terminal height when switching between terminals
                const terminalHeight = (_b = (_a = terminalWrapper.value) === null || _a === void 0 ? void 0 : _a.style.height) !== null && _b !== void 0 ? _b : 'unset';
                activeTerminal.value = terminal;
                isTerminalPanelOpened.value = true;
                setReadMessages(terminal);
                if (terminalHeight !== 'unset')
                    terminalWrapperStyles.value.height = terminalHeight;
            }
            // Focus on terminal if it is not readonly
            if (activeTerminal.value !== undefined &&
                !terminalStore.isReadOnly(activeTerminal.value)) {
                nextTick().then(() => { var _a; return (_a = document.getElementById('hterm-terminal')) === null || _a === void 0 ? void 0 : _a.focus(); });
            }
        };
        const clearTerminalOutput = (terminal) => {
            if (terminal !== undefined) {
                terminalStore.remove(terminal);
            }
            else {
                terminalStore.remove();
            }
        };
        return {
            toggleTerminalPanel,
            clearTerminalOutput,
            displayedTerminals,
            activeTerminal,
            isTerminalPanelOpened,
            resizer,
            terminalWrapper,
            pipelineSpanClasses,
            terminalWrapperStyles,
            resizerStyles,
            arrowHovered,
            terminalIndicatorColor,
        };
    },
});
